import { useEffect, useRef } from 'react';
import type { Feature, Polygon } from 'geojson';

import { grabCircle } from '../geometry';

type UseGrabToolArgs = {
  map: google.maps.Map | null;
  enabled: boolean;
  onGrab?: (circle: Feature<Polygon>) => void;
};

// While the grab tool is active, a map click emits a fixed-size circle (scaled
// to the current zoom) for the consumer to grab features within.
const useGrabTool = ({ map, enabled, onGrab }: UseGrabToolArgs) => {
  // Held in a ref so an unstable `onGrab` doesn't tear down and re-bind the
  // click listener every render — it only rebinds on map/enabled changes.
  const onGrabRef = useRef(onGrab);
  onGrabRef.current = onGrab;

  useEffect(() => {
    if (!map || !enabled) {
      return undefined;
    }
    const listener = map.addListener(
      'click',
      (event: google.maps.MapMouseEvent) => {
        const handleGrab = onGrabRef.current;
        if (!event.latLng || !handleGrab) {
          return;
        }

        const zoom = map.getZoom() ?? 12;
        const circle = grabCircle(
          { lat: event.latLng.lat(), lng: event.latLng.lng() },
          zoom,
        );
        handleGrab(circle);
      },
    );
    return () => listener.remove();
  }, [map, enabled]);
};

export default useGrabTool;
